// frontend/src/reservas/UserReservas.js
import React, { useState, useEffect, useCallback } from 'react';
import './UserReservas.css';
import { API_URL, API_ENDPOINTS } from '../config/api';
import {
    FaCalendarAlt, FaClipboardList, FaCheck, FaClock,
    FaTimesCircle, FaDollarSign, FaUsers, FaImage,
    FaInfoCircle, FaUpload, FaEye, FaTimes, FaMapMarkerAlt,
    FaCheckCircle, FaExclamationTriangle,
    FaChevronLeft, FaChevronRight
} from 'react-icons/fa';

const STATUS_LABELS = {
    pending: 'Pendiente',
    pre_approved: 'Pre-aprobada',
    payment_pending: 'Pago pendiente',
    payment_uploaded: 'Pago subido',
    approved: 'Aprobada',
    rejected: 'Rechazada',
    cancelled: 'Cancelada',
    completed: 'Completada',
    no_show: 'No se presento'
};

const STATUS_COLORS = {
    pending: '#ed8936',
    pre_approved: '#4299e1',
    payment_pending: '#d69e2e',
    payment_uploaded: '#dd6b20',
    approved: '#48bb78',
    rejected: '#e53e3e',
    cancelled: '#a0aec0',
    completed: '#38b2ac',
    no_show: '#718096'
};

const AREA_TYPES = {
    salon: 'Salon', piscina: 'Piscina', cancha: 'Cancha', bbq: 'BBQ',
    gimnasio: 'Gimnasio', terraza: 'Terraza', parqueadero: 'Parqueadero', otro: 'Otro'
};

const UserReservas = ({ user }) => {
    const [activeTab, setActiveTab] = useState('reservar');
    const [areas, setAreas] = useState([]);
    const [myReservations, setMyReservations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState(null);

    // Modal states
    const [selectedArea, setSelectedArea] = useState(null);
    const [showRulesModal, setShowRulesModal] = useState(false);
    const [showBookingForm, setShowBookingForm] = useState(false);
    const [rulesAccepted, setRulesAccepted] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [receiptReservationId, setReceiptReservationId] = useState(null);

    // Booking form
    const [bookingData, setBookingData] = useState({
        reservation_date: '',
        start_time: '',
        end_time: '',
        num_guests: 1,
        purpose: '',
        notes: ''
    });
    const [bookingLoading, setBookingLoading] = useState(false);
    const [availabilityChecked, setAvailabilityChecked] = useState(null);

    // Receipt upload
    const [receiptFile, setReceiptFile] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('transferencia');

    // Schedule visibility
    const [daySchedule, setDaySchedule] = useState([]);

    // Gallery
    const [galleryArea, setGalleryArea] = useState(null);
    const [galleryIndex, setGalleryIndex] = useState(0);

    const token = localStorage.getItem('token');

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 4000);
    };

    const authHeaders = useCallback(() => ({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    }), [token]);

    // ==================== FETCH ====================
    const fetchAreas = useCallback(async () => {
        try {
            const res = await fetch(`${API_ENDPOINTS.reservationAreas}?active=true`, { headers: authHeaders() });
            const data = await res.json();
            if (data.areas) setAreas(data.areas);
        } catch (err) { console.error('Error fetching areas:', err); }
    }, [authHeaders]);

    const fetchMyReservations = useCallback(async () => {
        try {
            const res = await fetch(API_ENDPOINTS.reservationsUser, { headers: authHeaders() });
            const data = await res.json();
            if (data.reservations) setMyReservations(data.reservations);
        } catch (err) { console.error('Error fetching reservations:', err); }
    }, [authHeaders]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchAreas(), fetchMyReservations()]);
            setLoading(false);
        };
        loadData();
    }, [fetchAreas, fetchMyReservations]);

    // ==================== BOOKING FLOW ====================
    const startBooking = (area) => {
        setSelectedArea(area);
        setRulesAccepted(false);
        setShowRulesModal(true);
    };

    const acceptRules = () => {
        setRulesAccepted(true);
        setShowRulesModal(false);
        setBookingData({
            reservation_date: '',
            start_time: selectedArea.available_from ? selectedArea.available_from.substring(0, 5) : '08:00',
            end_time: selectedArea.available_until ? selectedArea.available_until.substring(0, 5) : '22:00',
            num_guests: 1,
            purpose: '',
            notes: ''
        });
        setAvailabilityChecked(null);
        setDaySchedule([]);
        setShowBookingForm(true);
    };

    const fetchDaySchedule = async (areaId, date) => {
        try {
            const res = await fetch(
                `${API_ENDPOINTS.reservationSchedule}?area_id=${areaId}&date=${date}`,
                { headers: authHeaders() }
            );
            const data = await res.json();
            if (data.schedule) setDaySchedule(data.schedule);
        } catch (err) {
            setDaySchedule([]);
        }
    };

    const checkAvailability = async () => {
        if (!bookingData.reservation_date || !bookingData.start_time || !bookingData.end_time) return;
        try {
            const res = await fetch(
                `${API_ENDPOINTS.reservationAvailability}?area_id=${selectedArea.id}&date=${bookingData.reservation_date}&start_time=${bookingData.start_time}&end_time=${bookingData.end_time}`,
                { headers: authHeaders() }
            );
            const data = await res.json();
            setAvailabilityChecked(data.available);
        } catch (err) {
            setAvailabilityChecked(null);
        }
    };

    // Fetch schedule when date changes
    useEffect(() => {
        if (showBookingForm && bookingData.reservation_date && selectedArea) {
            fetchDaySchedule(selectedArea.id, bookingData.reservation_date);
        } else {
            setDaySchedule([]);
        }
    }, [bookingData.reservation_date, selectedArea?.id, showBookingForm]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (showBookingForm && bookingData.reservation_date && bookingData.start_time && bookingData.end_time && selectedArea) {
            const timer = setTimeout(checkAvailability, 500);
            return () => clearTimeout(timer);
        }
    }, [bookingData.reservation_date, bookingData.start_time, bookingData.end_time]); // eslint-disable-line react-hooks/exhaustive-deps

    const submitReservation = async (e) => {
        e.preventDefault();
        if (!rulesAccepted) {
            showNotif('Debe aceptar las normas del area', 'error');
            return;
        }
        setBookingLoading(true);
        try {
            const res = await fetch(API_ENDPOINTS.reservations, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    area_id: selectedArea.id,
                    ...bookingData,
                    rules_accepted: true
                })
            });
            const data = await res.json();
            if (res.ok) {
                showNotif(`Reserva enviada exitosamente! Ticket: ${data.reservation?.ticketNumber || ''}`);
                setShowBookingForm(false);
                setSelectedArea(null);
                fetchMyReservations();
                setActiveTab('mis-reservas');
            } else {
                showNotif(data.message || 'Error al crear reserva', 'error');
            }
        } catch (err) {
            showNotif('Error de conexion', 'error');
        } finally {
            setBookingLoading(false);
        }
    };

    // ==================== RECEIPT UPLOAD ====================
    const openReceiptUpload = (reservationId) => {
        setReceiptReservationId(reservationId);
        setReceiptFile(null);
        setPaymentMethod('transferencia');
        setShowReceiptModal(true);
    };

    const uploadReceipt = async (e) => {
        e.preventDefault();
        if (!receiptFile) {
            showNotif('Seleccione un comprobante', 'error');
            return;
        }
        try {
            const formData = new FormData();
            formData.append('receipt', receiptFile);
            formData.append('payment_method', paymentMethod);

            const res = await fetch(API_ENDPOINTS.reservationReceipt(receiptReservationId), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            if (res.ok) {
                showNotif('Comprobante subido. El admin validara su pago.');
                setShowReceiptModal(false);
                fetchMyReservations();
            } else {
                const data = await res.json();
                showNotif(data.message || 'Error al subir comprobante', 'error');
            }
        } catch (err) {
            showNotif('Error de conexion', 'error');
        }
    };

    // ==================== CANCEL ====================
    const cancelReservation = async (reservationId) => {
        if (!window.confirm('Cancelar esta solicitud de reserva?')) return;
        try {
            const res = await fetch(API_ENDPOINTS.reservationStatus(reservationId), {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ status: 'cancelled' })
            });
            if (res.ok) {
                showNotif('Reserva cancelada');
                fetchMyReservations();
            }
        } catch (err) { showNotif('Error', 'error'); }
    };

    // ==================== GALLERY ====================
    const openGallery = (area, index = 0) => {
        if (area.photos && area.photos.length > 0) {
            setGalleryArea(area);
            setGalleryIndex(index);
        }
    };
    const galleryNext = () => {
        if (galleryArea) setGalleryIndex((galleryIndex + 1) % galleryArea.photos.length);
    };
    const galleryPrev = () => {
        if (galleryArea) setGalleryIndex((galleryIndex - 1 + galleryArea.photos.length) % galleryArea.photos.length);
    };

    // ==================== FORMAT HELPERS ====================
    const formatDate = (d) => {
        if (!d) return 'N/A';
        return new Date(d).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    const formatTime = (t) => t ? t.substring(0, 5) : '';
    const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;

    const activeReservationsCount = myReservations.filter(
        r => ['pending', 'pre_approved', 'payment_uploaded', 'approved'].includes(r.status)
    ).length;

    // ==================== RENDER ====================
    if (loading) {
        return (
            <div className="urbanq-reservas-user-container">
                <div className="urbanq-reservas-user-loading">
                    <div className="urbanq-reservas-user-spinner"></div>
                    <p>Cargando reservas...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="urbanq-reservas-user-container urbanq-user-container">
            {/* Notification */}
            {notification && (
                <div className={`urbanq-reservas-user-notification ${notification.type}`}>
                    {notification.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
                    <span>{notification.message}</span>
                    <button onClick={() => setNotification(null)}><FaTimes /></button>
                </div>
            )}

            {/* Tabs */}
            <div className="urbanq-user-tabs">
                <button className={`urbanq-user-tab ${activeTab === 'reservar' ? 'active' : ''}`}
                    onClick={() => setActiveTab('reservar')}>
                    <FaCalendarAlt /> Reservar
                </button>
                <button className={`urbanq-user-tab ${activeTab === 'mis-reservas' ? 'active' : ''}`}
                    onClick={() => setActiveTab('mis-reservas')}>
                    <FaClipboardList /> Mis Reservas ({activeReservationsCount})
                </button>
            </div>

            {/* Stats */}
            <div className="urbanq-user-stats-grid">
                <div className="urbanq-user-stat-card info">
                    <div className="urbanq-user-stat-icon info">
                        <FaMapMarkerAlt />
                    </div>
                    <div className="urbanq-user-stat-content">
                        <div className="urbanq-user-stat-value">{areas.length}</div>
                        <div className="urbanq-user-stat-label">Areas Disponibles</div>
                    </div>
                </div>
                <div className="urbanq-user-stat-card success">
                    <div className="urbanq-user-stat-icon success">
                        <FaCalendarAlt />
                    </div>
                    <div className="urbanq-user-stat-content">
                        <div className="urbanq-user-stat-value">{activeReservationsCount}</div>
                        <div className="urbanq-user-stat-label">Reservas Activas</div>
                    </div>
                </div>
            </div>

            <div className="urbanq-reservas-user-content">

                {/* ========== TAB: RESERVAR ========== */}
                {activeTab === 'reservar' && (
                    <div className="urbanq-reservas-user-areas-grid">
                        {areas.length === 0 ? (
                            <div className="urbanq-reservas-user-empty">
                                <FaCalendarAlt />
                                <p>No hay areas disponibles para reservar</p>
                            </div>
                        ) : (
                            areas.map(area => (
                                <div key={area.id} className="urbanq-reservas-user-area-card">
                                    <div className="urbanq-reservas-user-area-image"
                                        onClick={() => openGallery(area, 0)}
                                        style={{ cursor: area.photos?.length > 0 ? 'pointer' : 'default' }}>
                                        {area.photos && area.photos.length > 0 ? (
                                            <div className="urbanq-reservas-user-photo-stack">
                                                {area.photos.slice(0, 3).map((photo, i) => (
                                                    <img key={i}
                                                        src={`${API_URL}${photo}`}
                                                        alt={`${area.name} ${i + 1}`}
                                                        className={`urbanq-reservas-user-stack-img stack-${i}`}
                                                        style={{
                                                            zIndex: 3 - i,
                                                            transform: `rotate(${i === 0 ? 0 : i === 1 ? -4 : 4}deg) translateX(${i === 0 ? 0 : i === 1 ? -8 : 8}px) scale(${1 - i * 0.04})`,
                                                        }}
                                                    />
                                                ))}
                                                {area.photos.length > 1 && (
                                                    <span className="urbanq-reservas-user-photo-count">
                                                        <FaImage /> {area.photos.length}
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="urbanq-reservas-user-area-placeholder"><FaImage /></div>
                                        )}
                                        <span className="urbanq-reservas-user-area-type-badge">
                                            {AREA_TYPES[area.type] || area.type}
                                        </span>
                                    </div>
                                    <div className="urbanq-reservas-user-area-body">
                                        <h3>{area.name}</h3>
                                        {area.description && <p className="urbanq-reservas-user-area-desc">{area.description}</p>}
                                        <div className="urbanq-reservas-user-area-meta">
                                            <span><FaUsers /> {area.capacity} personas</span>
                                            <span><FaDollarSign /> {area.tariff_type === 'gratis' ? 'Gratis' : formatCurrency(area.tariff)}</span>
                                            <span><FaClock /> {formatTime(area.available_from)} - {formatTime(area.available_until)}</span>
                                        </div>
                                        <button className="urbanq-reservas-user-book-btn" onClick={() => startBooking(area)}>
                                            <FaCalendarAlt /> Reservar
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* ========== TAB: MIS RESERVAS ========== */}
                {activeTab === 'mis-reservas' && (
                    <div>
                        {myReservations.length === 0 ? (
                            <div className="urbanq-user-empty">
                                <FaClipboardList className="urbanq-user-empty-icon" />
                                <h4>No tienes reservas aun</h4>
                                <p>Reserva un area comun desde la pestana Reservar</p>
                            </div>
                        ) : (
                            <div className="urbanq-user-table-card">
                                <div className="urbanq-user-table-header">
                                    <FaClipboardList />
                                    <h3>Mis Reservas ({myReservations.length})</h3>
                                </div>
                                <div className="table-responsive">
                                    <table className="urbanq-user-table">
                                        <thead>
                                            <tr>
                                                <th>Ticket</th>
                                                <th>Area</th>
                                                <th>Fecha</th>
                                                <th>Horario</th>
                                                <th>Monto</th>
                                                <th className="text-center">Estado</th>
                                                <th className="text-center">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {myReservations.map(r => (
                                                <tr key={r.id}>
                                                    <td className="urbanq-reservas-user-ticket">{r.ticket_number}</td>
                                                    <td>{r.area_name}</td>
                                                    <td>{formatDate(r.reservation_date)}</td>
                                                    <td>{formatTime(r.start_time)} - {formatTime(r.end_time)}</td>
                                                    <td>{formatCurrency(r.total_amount)}</td>
                                                    <td className="text-center">
                                                        <span className="urbanq-reservas-user-status"
                                                            style={{ background: STATUS_COLORS[r.status] + '20', color: STATUS_COLORS[r.status], borderColor: STATUS_COLORS[r.status] }}>
                                                            {STATUS_LABELS[r.status]}
                                                        </span>
                                                    </td>
                                                    <td className="urbanq-reservas-user-actions">
                                                        <button title="Ver detalle" onClick={() => {
                                                            setSelectedReservation(r);
                                                            setShowDetailModal(true);
                                                        }}><FaEye /></button>
                                                        {r.status === 'pre_approved' && (
                                                            <button title="Subir comprobante" className="upload"
                                                                onClick={() => openReceiptUpload(r.id)}>
                                                                <FaUpload />
                                                            </button>
                                                        )}
                                                        {r.status === 'pending' && (
                                                            <button title="Cancelar" className="cancel"
                                                                onClick={() => cancelReservation(r.id)}>
                                                                <FaTimesCircle />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ========== MODAL: NORMAS ========== */}
            {showRulesModal && selectedArea && (
                <div className="urbanq-reservas-user-modal-overlay" onClick={() => setShowRulesModal(false)}>
                    <div className="urbanq-reservas-user-modal" onClick={e => e.stopPropagation()}>
                        <div className="urbanq-reservas-user-modal-header">
                            <h2><FaInfoCircle /> Normas de Uso - {selectedArea.name}</h2>
                            <button onClick={() => setShowRulesModal(false)}><FaTimes /></button>
                        </div>
                        <div className="urbanq-reservas-user-modal-body">
                            <div className="urbanq-reservas-user-rules-content">
                                {selectedArea.rules ? (
                                    <div className="urbanq-reservas-user-rules-text">{selectedArea.rules}</div>
                                ) : (
                                    <p className="urbanq-reservas-user-no-rules">Esta area no tiene normas especificas registradas.</p>
                                )}
                            </div>
                            <div className="urbanq-reservas-user-rules-info">
                                <div><FaUsers /> Capacidad: {selectedArea.capacity} personas</div>
                                <div><FaClock /> Horario: {formatTime(selectedArea.available_from)} - {formatTime(selectedArea.available_until)}</div>
                                <div><FaDollarSign /> Tarifa: {selectedArea.tariff_type === 'gratis' ? 'Gratis' : formatCurrency(selectedArea.tariff)}</div>
                            </div>
                            <label className="urbanq-reservas-user-rules-accept">
                                <input type="checkbox" checked={rulesAccepted} onChange={e => setRulesAccepted(e.target.checked)} />
                                <span>He leido y acepto las normas de uso de esta area</span>
                            </label>
                            <div className="urbanq-reservas-user-modal-footer">
                                <button className="urbanq-reservas-user-btn-secondary" onClick={() => setShowRulesModal(false)}>
                                    Cancelar
                                </button>
                                <button className="urbanq-reservas-user-btn-primary" disabled={!rulesAccepted}
                                    onClick={acceptRules}>
                                    <FaCheck /> Aceptar y Continuar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== MODAL: BOOKING FORM ========== */}
            {showBookingForm && selectedArea && (
                <div className="urbanq-reservas-user-modal-overlay" onClick={() => setShowBookingForm(false)}>
                    <div className="urbanq-reservas-user-modal" onClick={e => e.stopPropagation()}>
                        <div className="urbanq-reservas-user-modal-header">
                            <h2><FaCalendarAlt /> Reservar - {selectedArea.name}</h2>
                            <button onClick={() => setShowBookingForm(false)}><FaTimes /></button>
                        </div>
                        <form onSubmit={submitReservation} className="urbanq-reservas-user-modal-body">
                            <div className="urbanq-reservas-user-form-grid">
                                <div className="urbanq-reservas-user-form-group">
                                    <label>Fecha de reserva *</label>
                                    <input type="date" required value={bookingData.reservation_date}
                                        min={new Date(Date.now() + Math.max(0, selectedArea.advance_days || 0) * 86400000).toISOString().split('T')[0]}
                                        max={new Date(Date.now() + (selectedArea.max_advance_days || 60) * 86400000).toISOString().split('T')[0]}
                                        onChange={e => setBookingData({ ...bookingData, reservation_date: e.target.value })} />
                                    <span className="urbanq-reservas-user-form-hint">
                                        Disponible desde {new Date(Date.now() + Math.max(0, selectedArea.advance_days || 0) * 86400000).toLocaleDateString('es-EC', {day:'2-digit', month:'short'})} hasta {new Date(Date.now() + (selectedArea.max_advance_days || 60) * 86400000).toLocaleDateString('es-EC', {day:'2-digit', month:'short'})}
                                    </span>
                                </div>
                                <div className="urbanq-reservas-user-form-group">
                                    <label>Numero de invitados</label>
                                    <input type="number" min="1" max={selectedArea.capacity || 100}
                                        value={bookingData.num_guests}
                                        onChange={e => setBookingData({ ...bookingData, num_guests: parseInt(e.target.value) || 1 })} />
                                </div>
                                <div className="urbanq-reservas-user-form-group">
                                    <label>Hora inicio *</label>
                                    <input type="time" required value={bookingData.start_time}
                                        min={selectedArea.available_from ? selectedArea.available_from.substring(0, 5) : '06:00'}
                                        max={selectedArea.available_until ? selectedArea.available_until.substring(0, 5) : '23:00'}
                                        onChange={e => setBookingData({ ...bookingData, start_time: e.target.value })} />
                                </div>
                                <div className="urbanq-reservas-user-form-group">
                                    <label>Hora fin *</label>
                                    <input type="time" required value={bookingData.end_time}
                                        min={bookingData.start_time || (selectedArea.available_from ? selectedArea.available_from.substring(0, 5) : '06:00')}
                                        max={selectedArea.available_until ? selectedArea.available_until.substring(0, 5) : '23:00'}
                                        onChange={e => setBookingData({ ...bookingData, end_time: e.target.value })} />
                                </div>
                            </div>

                            {/* Schedule visibility - occupied time slots */}
                            {bookingData.reservation_date && daySchedule.length > 0 && (
                                <div className="urbanq-reservas-user-schedule-panel">
                                    <div className="urbanq-reservas-user-schedule-title">
                                        <FaClock /> Horarios ocupados el {new Date(bookingData.reservation_date + 'T12:00:00').toLocaleDateString('es-EC', {day:'2-digit', month:'short'})}
                                    </div>
                                    <div className="urbanq-reservas-user-schedule-slots">
                                        {daySchedule.map((slot, i) => (
                                            <div key={i} className={`urbanq-reservas-user-schedule-slot ${slot.status}`}>
                                                <span className="urbanq-reservas-user-schedule-time">
                                                    {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                                                </span>
                                                <span className="urbanq-reservas-user-schedule-status">
                                                    {STATUS_LABELS[slot.status] || slot.status}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {bookingData.reservation_date && daySchedule.length === 0 && (
                                <div className="urbanq-reservas-user-schedule-panel free">
                                    <FaCheckCircle /> Todo el horario esta disponible para esta fecha
                                </div>
                            )}

                            {availabilityChecked !== null && (
                                <div className={`urbanq-reservas-user-availability ${availabilityChecked ? 'available' : 'unavailable'}`}>
                                    {availabilityChecked ? (
                                        <><FaCheckCircle /> Horario seleccionado disponible</>
                                    ) : (
                                        <><FaTimesCircle /> Horario no disponible. Elija otro.</>
                                    )}
                                </div>
                            )}

                            <div className="urbanq-reservas-user-form-group full-width">
                                <label>Motivo / Evento</label>
                                <input type="text" value={bookingData.purpose}
                                    onChange={e => setBookingData({ ...bookingData, purpose: e.target.value })}
                                    placeholder="Ej: Cumpleanos, reunion familiar..." />
                            </div>

                            <div className="urbanq-reservas-user-form-group full-width">
                                <label>Notas adicionales</label>
                                <textarea rows="3" value={bookingData.notes}
                                    onChange={e => setBookingData({ ...bookingData, notes: e.target.value })}
                                    placeholder="Alguna observacion o requerimiento..." />
                            </div>

                            <div className="urbanq-reservas-user-booking-summary">
                                <strong>Resumen:</strong>
                                <span>Area: {selectedArea.name}</span>
                                <span>Tarifa: {selectedArea.tariff_type === 'gratis' ? 'Gratis' : formatCurrency(selectedArea.tariff)}</span>
                                {selectedArea.deposit > 0 && <span>Deposito: {formatCurrency(selectedArea.deposit)}</span>}
                            </div>

                            <div className="urbanq-reservas-user-modal-footer">
                                <button type="button" className="urbanq-reservas-user-btn-secondary"
                                    onClick={() => setShowBookingForm(false)}>Cancelar</button>
                                <button type="submit" className="urbanq-reservas-user-btn-primary"
                                    disabled={bookingLoading || availabilityChecked === false}>
                                    {bookingLoading ? 'Enviando...' : 'Enviar Solicitud'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ========== MODAL: DETAIL ========== */}
            {showDetailModal && selectedReservation && (
                <div className="urbanq-reservas-user-modal-overlay" onClick={() => setShowDetailModal(false)}>
                    <div className="urbanq-reservas-user-modal" onClick={e => e.stopPropagation()}>
                        <div className="urbanq-reservas-user-modal-header">
                            <h2>Detalle - {selectedReservation.ticket_number}</h2>
                            <button onClick={() => setShowDetailModal(false)}><FaTimes /></button>
                        </div>
                        <div className="urbanq-reservas-user-modal-body">
                            <div className="urbanq-reservas-user-detail-grid">
                                <div className="urbanq-reservas-user-detail-item">
                                    <label>Area</label>
                                    <span>{selectedReservation.area_name}</span>
                                </div>
                                <div className="urbanq-reservas-user-detail-item">
                                    <label>Fecha</label>
                                    <span>{formatDate(selectedReservation.reservation_date)}</span>
                                </div>
                                <div className="urbanq-reservas-user-detail-item">
                                    <label>Horario</label>
                                    <span>{formatTime(selectedReservation.start_time)} - {formatTime(selectedReservation.end_time)}</span>
                                </div>
                                <div className="urbanq-reservas-user-detail-item">
                                    <label>Invitados</label>
                                    <span>{selectedReservation.num_guests}</span>
                                </div>
                                <div className="urbanq-reservas-user-detail-item">
                                    <label>Monto</label>
                                    <span>{formatCurrency(selectedReservation.total_amount)}</span>
                                </div>
                                <div className="urbanq-reservas-user-detail-item">
                                    <label>Estado</label>
                                    <span className="urbanq-reservas-user-status"
                                        style={{ background: STATUS_COLORS[selectedReservation.status] + '20', color: STATUS_COLORS[selectedReservation.status], borderColor: STATUS_COLORS[selectedReservation.status] }}>
                                        {STATUS_LABELS[selectedReservation.status]}
                                    </span>
                                </div>
                                {selectedReservation.purpose && (
                                    <div className="urbanq-reservas-user-detail-item full">
                                        <label>Motivo</label>
                                        <span>{selectedReservation.purpose}</span>
                                    </div>
                                )}
                                {selectedReservation.admin_notes && (
                                    <div className="urbanq-reservas-user-detail-item full">
                                        <label>Notas del administrador</label>
                                        <span>{selectedReservation.admin_notes}</span>
                                    </div>
                                )}
                            </div>
                            {selectedReservation.status === 'pre_approved' && (
                                <div className="urbanq-reservas-user-detail-action">
                                    <button className="urbanq-reservas-user-btn-primary"
                                        onClick={() => {
                                            setShowDetailModal(false);
                                            openReceiptUpload(selectedReservation.id);
                                        }}>
                                        <FaUpload /> Subir Comprobante de Pago
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ========== MODAL: GALERIA FOTOS ========== */}
            {galleryArea && (
                <div className="urbanq-reservas-user-modal-overlay urbanq-reservas-gallery-overlay"
                    onClick={() => setGalleryArea(null)}>
                    <div className="urbanq-reservas-gallery-container" onClick={e => e.stopPropagation()}>
                        <button className="urbanq-reservas-gallery-close" onClick={() => setGalleryArea(null)}>
                            <FaTimes />
                        </button>
                        <h3 className="urbanq-reservas-gallery-title">{galleryArea.name}</h3>
                        <div className="urbanq-reservas-gallery-viewport">
                            {galleryArea.photos.length > 1 && (
                                <button className="urbanq-reservas-gallery-nav prev" onClick={galleryPrev}>
                                    <FaChevronLeft />
                                </button>
                            )}
                            <div className="urbanq-reservas-gallery-3d-scene">
                                {galleryArea.photos.map((photo, i) => {
                                    const offset = i - galleryIndex;
                                    const absOffset = Math.abs(offset);
                                    if (absOffset > 2) return null;
                                    return (
                                        <div key={i}
                                            className={`urbanq-reservas-gallery-slide ${offset === 0 ? 'active' : ''}`}
                                            style={{
                                                transform: `translateX(${offset * 60}%) translateZ(${-absOffset * 120}px) rotateY(${offset * -15}deg)`,
                                                opacity: offset === 0 ? 1 : 0.5,
                                                zIndex: 10 - absOffset,
                                                filter: offset === 0 ? 'none' : 'brightness(0.6)',
                                            }}
                                            onClick={() => setGalleryIndex(i)}>
                                            <img src={`${API_URL}${photo}`} alt={`${galleryArea.name} ${i + 1}`} />
                                        </div>
                                    );
                                })}
                            </div>
                            {galleryArea.photos.length > 1 && (
                                <button className="urbanq-reservas-gallery-nav next" onClick={galleryNext}>
                                    <FaChevronRight />
                                </button>
                            )}
                        </div>
                        <div className="urbanq-reservas-gallery-dots">
                            {galleryArea.photos.map((_, i) => (
                                <span key={i}
                                    className={`urbanq-reservas-gallery-dot ${i === galleryIndex ? 'active' : ''}`}
                                    onClick={() => setGalleryIndex(i)} />
                            ))}
                        </div>
                        <p className="urbanq-reservas-gallery-counter">{galleryIndex + 1} / {galleryArea.photos.length}</p>
                    </div>
                </div>
            )}

            {/* ========== MODAL: RECEIPT UPLOAD ========== */}
            {showReceiptModal && (
                <div className="urbanq-reservas-user-modal-overlay" onClick={() => setShowReceiptModal(false)}>
                    <div className="urbanq-reservas-user-modal urbanq-reservas-user-modal-sm" onClick={e => e.stopPropagation()}>
                        <div className="urbanq-reservas-user-modal-header">
                            <h2><FaUpload /> Subir Comprobante</h2>
                            <button onClick={() => setShowReceiptModal(false)}><FaTimes /></button>
                        </div>
                        <form onSubmit={uploadReceipt} className="urbanq-reservas-user-modal-body">
                            <div className="urbanq-reservas-user-form-group full-width">
                                <label>Metodo de pago</label>
                                <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                                    <option value="transferencia">Transferencia bancaria</option>
                                    <option value="efectivo">Efectivo</option>
                                    <option value="paypal">PayPal</option>
                                    <option value="otro">Otro</option>
                                </select>
                            </div>
                            <div className="urbanq-reservas-user-form-group full-width">
                                <label>Comprobante (imagen o PDF) *</label>
                                <input type="file" required accept="image/*,.pdf"
                                    onChange={e => setReceiptFile(e.target.files[0])} />
                            </div>
                            <div className="urbanq-reservas-user-modal-footer">
                                <button type="button" className="urbanq-reservas-user-btn-secondary"
                                    onClick={() => setShowReceiptModal(false)}>Cancelar</button>
                                <button type="submit" className="urbanq-reservas-user-btn-primary">
                                    <FaUpload /> Subir Comprobante
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserReservas;
